import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { productServices, categories } from '@/mocks/clsqData';

const categorySchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'clsq产品分类 - 核心产品与服务',
  description: '浏览clsq全部产品与服务分类，包括核心产品、解决方案和技术支持，找到最适合您企业的产品。',
  url: 'https://clsq.com/categories',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://clsq.com' },
    { '@type': 'ListItem', position: 2, name: '产品分类', item: 'https://clsq.com/categories' },
  ],
};

export default function CategoriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'products';
  const [filteredProducts, setFilteredProducts] = useState(productServices);

  useEffect(() => {
    setFilteredProducts(productServices.filter((p) => p.category === activeTab));
  }, [activeTab]);

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const activeCategory = categories.find((c) => c.id === activeTab);

  return (
    <>
      <SEOHead
        title="clsq产品分类 - 核心产品与服务 | clsq官方"
        description="浏览clsq全部产品与服务分类，包括核心产品、解决方案和技术支持，找到最适合您企业的产品。"
        keywords="clsq产品,clsq服务,企业解决方案,智能产品,技术支持"
        canonicalUrl="https://clsq.com/categories"
        schemaData={{ ...categorySchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-16">
          {/* Breadcrumb */}
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto pt-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">产品分类</span>
            </nav>
          </div>

          {/* Header */}
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto pt-10 pb-6">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
              产品与服务
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">
              探索clsq完整的产品矩阵，从智能解决方案到专业技术支持，全面满足您的业务需求
            </p>
          </div>

          {/* Category Tabs */}
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-10">
            <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/60">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabChange(cat.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === cat.id
                      ? 'bg-background-50 text-primary-600 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-20">
            {activeCategory && (
              <h2 className="text-lg font-semibold text-foreground-800 mb-6">
                {activeCategory.label}
                <span className="text-sm font-normal text-foreground-500 ml-2">
                  共 {filteredProducts.length} 个产品
                </span>
              </h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-product-shop>
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 md:p-6">
                    <h3 className="text-lg font-semibold text-foreground-900 mb-2">{product.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{product.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {product.features.map((f) => (
                        <span
                          key={f}
                          className="px-2.5 py-1 bg-secondary-100/80 text-secondary-700 rounded-md text-xs font-medium"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/60">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无产品</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}